import type { ChangeEvent, KeyboardEvent } from 'react';
import type { IconifyName } from 'src/components/iconify/register-icons';

import { varAlpha } from 'minimal-shared/utils';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

export type ContentType = 'simulation' | 'exercise' | 'quiz';

export const CONTENT_TYPE_OPTIONS: {
  value: ContentType;
  label: string;
  icon: IconifyName;
  disabled?: boolean;
}[] = [
  { value: 'simulation', label: 'Simulation', icon: 'solar:play-circle-bold' },
  { value: 'exercise', label: 'Exercise', icon: 'solar:pen-bold', disabled: true },
  { value: 'quiz', label: 'Quiz', icon: 'solar:bill-list-bold', disabled: true },
];

export type AiChatInputAttachment = {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
  preview?: string;
  base64Data?: string;
};

type Props = {
  onSendMessage: (message: string, attachments?: AiChatInputAttachment[]) => void;
  disabled?: boolean;
  placeholder?: string;
  contentType?: ContentType;
  onContentTypeChange?: (type: ContentType) => void;
};

export function AiChatInput({
  onSendMessage,
  disabled,
  placeholder,
  contentType = 'simulation',
  onContentTypeChange,
}: Props) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<AiChatInputAttachment[]>([]);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedTypeOption =
    CONTENT_TYPE_OPTIONS.find((opt) => opt.value === contentType) || CONTENT_TYPE_OPTIONS[0];

  const handleTypeMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setTypeMenuAnchor(event.currentTarget);
  }, []);

  const handleTypeMenuClose = useCallback(() => {
    setTypeMenuAnchor(null);
  }, []);

  const handleTypeSelect = useCallback(
    (type: ContentType) => {
      onContentTypeChange?.(type);
      setTypeMenuAnchor(null);
    },
    [onContentTypeChange]
  );

  const handleSendMessage = useCallback(() => {
    if ((message.trim() || attachments.length > 0) && !disabled) {
      onSendMessage(message.trim(), attachments.length > 0 ? attachments : undefined);
      setMessage('');
      setAttachments([]);
    }
  }, [message, attachments, onSendMessage, disabled]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleFileSelect = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const filePromises = Array.from(files).map(async (file) => {
        const attachment: AiChatInputAttachment = {
          id: `${Date.now()}-${file.name}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file,
        };

        // Create preview URL for images
        if (file.type.startsWith('image/')) {
          attachment.preview = URL.createObjectURL(file);
        }

        // Read file as base64 for API submission
        const base64Data = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            // Remove the data:mime/type;base64, prefix
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(file);
        });
        attachment.base64Data = base64Data;

        return attachment;
      });

      const newAttachments = await Promise.all(filePromises);
      setAttachments((prev) => [...prev, ...newAttachments]);
    }
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Cleanup preview URLs when attachments are removed or component unmounts
  useEffect(
    () => () => {
      attachments.forEach((attachment) => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });
    },
    [attachments]
  );

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
  }, []);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <Box
      sx={{
        p: 2,
        width: '100%',
        maxWidth: 800,
        mx: 'auto',
      }}
    >
      <Box
        sx={(theme) => ({
          bgcolor: varAlpha(theme.vars.palette.background.defaultChannel, 0.9),
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.vars.customShadows.card,
        })}
      >
        {/* Attachments preview */}
        {attachments.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              px: 2,
              pt: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {attachments.map((attachment) => {
              const isImage = attachment.type.startsWith('image/');
              const isPdf = attachment.type === 'application/pdf';

              return (
                <Box
                  key={attachment.id}
                  sx={(theme) => ({
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: isImage && attachment.preview ? 0.5 : 1.5,
                    py: 0.5,
                    bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.12),
                    borderRadius: 1,
                    color: 'text.secondary',
                    typography: 'caption',
                  })}
                >
                  {isImage && attachment.preview ? (
                    <Box
                      component="img"
                      src={attachment.preview}
                      alt={attachment.name}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 0.5,
                      }}
                    />
                  ) : (
                    <Iconify
                      icon="solar:file-text-bold"
                      width={16}
                      sx={{ color: isPdf ? 'error.main' : 'text.disabled' }}
                    />
                  )}
                  <Box
                    sx={{
                      maxWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {attachment.name}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAttachment(attachment.id)}
                    sx={{ color: 'text.disabled', p: 0.25 }}
                  >
                    <Iconify icon="solar:close-circle-bold" width={14} />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}

        {/* Text input area */}
        <InputBase
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Ask Lumi to create something...'}
          disabled={disabled}
          sx={{
            px: 2,
            py: 2,
            color: 'text.primary',
            '& .MuiInputBase-input': {
              '&::placeholder': {
                color: 'text.disabled',
                opacity: 1,
              },
            },
          }}
        />

        {/* Bottom toolbar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.5,
            pb: 1.5,
          }}
        >
          {/* Left side buttons */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<Iconify icon="eva:attach-2-fill" width={18} />}
              onClick={handleAttachClick}
              disabled={disabled}
              sx={(theme) => ({
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 1.5,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                },
              })}
            >
              Attach
            </Button>

            <Button
              size="small"
              startIcon={<Iconify icon={selectedTypeOption.icon} width={18} />}
              endIcon={<Iconify icon="eva:chevron-down-fill" width={16} />}
              onClick={handleTypeMenuOpen}
              disabled={disabled}
              sx={(theme) => ({
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 1.5,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                },
              })}
            >
              {selectedTypeOption.label}
            </Button>

            <Menu
              anchorEl={typeMenuAnchor}
              open={Boolean(typeMenuAnchor)}
              onClose={handleTypeMenuClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              {CONTENT_TYPE_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.value === contentType}
                  disabled={option.disabled}
                  onClick={() => handleTypeSelect(option.value)}
                  sx={{ gap: 1 }}
                >
                  <Iconify icon={option.icon} width={20} />
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </Stack>

          {/* Right side buttons */}
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleSendMessage}
              disabled={(!message.trim() && attachments.length === 0) || disabled}
              sx={(theme) => ({
                color: 'primary.contrastText',
                bgcolor: 'primary.main',
                borderRadius: '50%',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  color: 'text.disabled',
                  bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.24),
                },
              })}
            >
              <Iconify icon="eva:arrow-upward-fill" width={20} />
            </IconButton>
          </Stack>
        </Stack>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
        />
      </Box>
    </Box>
  );
}
